import { EFinancingProgram, EWorksheetStatus } from '@if/core/types'
import { FormContainer, useCan } from '@if/mui'
import { access } from '@src/access'
import { FinancingConfigDto, useMerchantFinancingConfig } from '@src/data/api/configs-api/financing-config-api'
import { useProductsProviders } from '@src/data/api/products-api/products-api'
import { usePostProductsWorksheet, usePutProductsWorksheet } from '@src/data/api/worksheet-api/product-worksheet-api'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { buildProductWorksheetSchema, ProductsWorksheet } from '@src/types/WorksheetSchema'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import WorksheetResetAction from '../components/WorksheetResetAction'
import WorksheetSkeleton from '../components/WorksheetSkeleton'
import ProductsWorksheetBody from './Components/ProductsWorksheetBody'

interface Props {
  merchant: Merchant
  creditApplication: CreditApplication
  merchantPayments: { label: string; value: string; id: string }[]
}

interface FormProps extends Props {
  financingConfig: FinancingConfigDto | undefined
  productsCapEnabled: boolean
}

const ProductsWorksheetForm = ({ merchant, creditApplication, financingConfig, productsCapEnabled }: FormProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const isSymTechPrime = creditApplication.isSymTechPrime === true
  const ProductWorksheetSchema = useMemo(
    () => buildProductWorksheetSchema({ financingConfig, featureEnabled: productsCapEnabled }, isSymTechPrime),
    [financingConfig, productsCapEnabled, isSymTechPrime],
  )
  // Cast a worksheet through the schema so its wire-string dates become `DateOnly`
  // (what the pickers require). Reused for the loaded worksheet and the post-split reset.
  const castWorksheet = useCallback(
    (worksheet: ProductsWorksheet): ProductsWorksheet =>
      ProductWorksheetSchema.cast(worksheet) as unknown as ProductsWorksheet,
    [ProductWorksheetSchema],
  )
  const defaultValues = creditApplication.worksheet
    ? castWorksheet(creditApplication.worksheet as unknown as ProductsWorksheet)
    : (ProductWorksheetSchema.getDefault() as unknown as ProductsWorksheet)
  // Derive from the raw worksheet (pre-cast): casting backfills `endDate` to its
  // schema default (null), which would otherwise flip this from "not present".
  const hasContractEndDate =
    (creditApplication.worksheet as unknown as ProductsWorksheet | undefined)?.replacementOrGapInsurance?.endDate !==
    undefined

  const isDraftWorksheet = creditApplication.worksheet?.status === EWorksheetStatus.Draft

  const [postWorksheet] = usePostProductsWorksheet()
  const [putWorksheet] = usePutProductsWorksheet()
  const [productsProviders, isLoadingProductProviders] = useProductsProviders()
  const [fundingReady, setFundingReady] = useState(false)

  const saveWorksheet = async (formData: ProductsWorksheet) => {
    if (!fundingReady) {
      return false
    }
    formData.creditApplicationId = creditApplication.id
    if (creditApplication.worksheet == null) {
      await postWorksheet({ worksheet: formData })
    } else {
      await putWorksheet({ worksheet: formData })
    }
    navigate(`/credits/${creditApplication.financingProgramId}/${creditApplication.id}`, { replace: true })
  }

  if (isLoadingProductProviders) {
    return <WorksheetSkeleton />
  }

  return (
    <>
      <FormContainer
        mode={creditApplication.worksheet && !isDraftWorksheet ? 'edit' : 'create'}
        useFormSetup={() => ({ schema: ProductWorksheetSchema, defaultValues })}
        onSubmit={saveWorksheet}
        successMessage={t('worksheet.saveSuccess')}
        pageActions
        submitLabel={t('worksheet.createContract')}
        actions={<WorksheetResetAction />}
      >
        <ProductsWorksheetBody
          merchant={merchant}
          creditApplication={creditApplication}
          productsCapEnabled={productsCapEnabled}
          productsProviders={productsProviders}
          hasContractEndDate={hasContractEndDate}
          onFundingReadyChange={setFundingReady}
          castWorksheet={castWorksheet}
        />
      </FormContainer>
    </>
  )
}

const ProductsWorksheetPage = (props: Props) => {
  const productsCapEnabled = useCan(access.productChargebackConfig)
  const [financingConfig, isFetchingConfig] = useMerchantFinancingConfig(props.merchant.id, EFinancingProgram.Products)

  if (productsCapEnabled && isFetchingConfig && !financingConfig) {
    return <WorksheetSkeleton />
  }

  return <ProductsWorksheetForm {...props} financingConfig={financingConfig} productsCapEnabled={productsCapEnabled} />
}

export default ProductsWorksheetPage
